import { Link } from 'react-router-dom'

import {
  EmptyState,
  InitialsAvatar,
  ProgressTile,
  SectionCard,
} from '@/features/admin/components/AdminDashboardInsights'
import type { RecruiterDashboard, RecruiterTeamActivityRow } from '@/features/recruiter/api/dashboard.api'
import { formatRelativeTime, humanizeEnum } from '@/utils/format'

function formatDays(days: number) {
  return `${days % 1 === 0 ? days : days.toFixed(1)} day${days === 1 ? '' : 's'}`
}

// ─── Tiles ───────────────────────────────────────────────────────────────────

function LinkTile({
  label,
  value,
  caption,
  tone,
  iconClass,
  to,
}: {
  label: string
  value: string
  caption: string
  tone: string
  iconClass: string
  to?: string
}) {
  const body = (
    <div className="card-body">
      <div className="d-flex align-items-start justify-content-between mb-3">
        <div>
          <p className="fs-13 text-muted mb-1">{label}</p>
          <p className="fs-28 fw-bold mb-0 lh-1">{value}</p>
        </div>
        <div
          className={`rounded-3 d-flex align-items-center justify-content-center bg-${tone}-transparent text-${tone}`}
          style={{ width: 52, height: 52, flexShrink: 0 }}
        >
          <i className={`ti ${iconClass} fs-22`} />
        </div>
      </div>
      <p className={`fs-12 mb-0 ${to ? 'text-primary' : 'text-muted'}`}>
        {caption} {to && <i className="ti ti-arrow-right" />}
      </p>
    </div>
  )

  return (
    <div className="col-xl-4 col-md-6">
      {to ? (
        <Link to={to} className="card custom-card h-100 text-decoration-none text-reset" aria-label={`${label}: ${value}. ${caption}`}>
          {body}
        </Link>
      ) : (
        <div className="card custom-card h-100">{body}</div>
      )}
    </div>
  )
}

export function RecruiterInsightTiles({ data }: { data: RecruiterDashboard }) {
  const { openPositions, timeToHire, starredCount } = data
  const filledPct = openPositions.openings > 0 ? (openPositions.filled / openPositions.openings) * 100 : 0

  return (
    <div className="row g-3 mb-3">
      <LinkTile
        label="Starred Candidates"
        value={starredCount.toLocaleString()}
        caption="View your starred candidates"
        tone="warning"
        iconClass="ti-star"
        to="/recruiter/all-candidates?starred=1"
      />
      <ProgressTile
        label="Open Positions"
        value={openPositions.remaining.toLocaleString()}
        caption={`${openPositions.filled.toLocaleString()} of ${openPositions.openings.toLocaleString()} openings on live jobs filled`}
        progress={filledPct}
        tone="primary"
        iconClass="ti-armchair"
      />
      <LinkTile
        label="Average Time to Hire"
        value={timeToHire.averageDays == null ? '—' : formatDays(timeToHire.averageDays)}
        caption={
          timeToHire.hires > 0
            ? `From application to hire, across ${timeToHire.hires.toLocaleString()} hire${timeToHire.hires === 1 ? '' : 's'}`
            : 'No hires yet'
        }
        tone="info"
        iconClass="ti-clock-hour-4"
      />
    </div>
  )
}

// ─── Time to hire, per job ───────────────────────────────────────────────────

export function TimeToHireChart({ timeToHire }: { timeToHire: RecruiterDashboard['timeToHire'] }) {
  const rows = timeToHire.byJob
  const max = Math.max(...rows.map((r) => r.averageDays), 1)
  const average = timeToHire.averageDays

  return (
    <SectionCard
      title="Time to Hire"
      subtitle="Average days from application to hire, for your jobs with the most hires"
    >
      {rows.length === 0 ? (
        <EmptyState icon="ti-clock-hour-4" text="No hires yet." />
      ) : (
        <div className="d-flex flex-column gap-3">
          {rows.map((row) => (
            <div
              key={row.jobId}
              title={`${row.title}: ${formatDays(row.averageDays)} on average over ${row.hires} hire${row.hires === 1 ? '' : 's'}`}
            >
              <div className="d-flex align-items-baseline justify-content-between gap-2 mb-1">
                <Link to={`/recruiter/jobs/${row.jobId}/applicants`} className="fs-13 fw-medium text-truncate">
                  {row.title}
                </Link>
                <span className="fs-13 text-nowrap">
                  <strong>{formatDays(row.averageDays)}</strong>
                  <span className="text-muted fs-12 ms-2">
                    {row.hires} hire{row.hires === 1 ? '' : 's'}
                  </span>
                </span>
              </div>
              <div className="position-relative">
                <div
                  className="bg-info rounded-end-1"
                  style={{ width: `${Math.max((row.averageDays / max) * 100, 1)}%`, height: 14 }}
                />
              </div>
            </div>
          ))}
          {average != null && (
            <p className="fs-12 text-muted mb-0 pt-2 border-top">
              Average across all your hires: <strong>{formatDays(average)}</strong>
            </p>
          )}
        </div>
      )}
    </SectionCard>
  )
}

// ─── Team activity ───────────────────────────────────────────────────────────

export function TeamActivity({ rows }: { rows: RecruiterTeamActivityRow[] }) {
  return (
    <SectionCard
      title="Team Activity"
      subtitle="What each recruiter in your organisation has done"
      bodyClass="card-body p-0"
      action={
        <Link to="/recruiter/team" className="btn btn-sm btn-primary-light">
          Manage Team
        </Link>
      }
    >
      {rows.length === 0 ? (
        <EmptyState icon="ti-users" text="No team members yet." />
      ) : (
        <div className="table-responsive">
          <table className="table text-nowrap mb-0">
            <thead>
              <tr>
                <th className="fs-12">Recruiter</th>
                <th className="fs-12 text-end">Jobs Posted</th>
                <th className="fs-12 text-end">Applications Processed</th>
                <th className="fs-12 text-end">Hires</th>
                <th className="fs-12">Last Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.userId}>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <InitialsAvatar name={row.name} index={i} />
                      <div>
                        <p className="fs-13 fw-semibold mb-0">{row.name}</p>
                        <span className="fs-11 text-muted">
                          {humanizeEnum(row.userType)}
                          {!row.isActive && <span className="badge bg-secondary-transparent fs-10 ms-1">Inactive</span>}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="text-end fs-13">{row.jobsPosted.toLocaleString()}</td>
                  <td className="text-end fs-13">{row.applicationsProcessed.toLocaleString()}</td>
                  <td className="text-end fs-13 fw-semibold">{row.hires.toLocaleString()}</td>
                  <td className="fs-12 text-muted">
                    {row.lastActionAt ? formatRelativeTime(row.lastActionAt) : 'No activity yet'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  )
}
